import { Button, Typography, VerticalDivider } from '@smartgift/frontend-toolkit';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SkeletonLoaders } from '@hero/components';
import {
  TotalEstimatedSpendModal,
  SpentToDateModal,
  TotalEstimatedSpendAutomationModal
} from '@hero/components/modals';
import {
  ORGANIZATION_TYPES,
  ORDER_STATUSES,
  RECOGNITION_STATUSES,
  AUTOMATION_STATUSES
} from '@hero/constants';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { formatCurrency } from '@hero/utils/helper';

import { OrderStatusBadge } from '../OrderStatusBadge';

import { CcPaymentNotifications } from './CcPaymentNotifications';
import * as S from './style';

function LiveCostSummary({
  isCCPayment,
  adjustedEstimatedCharge,
  estimatedCharges,
  spendingSummary,
  featureType,
  authorization,
  isLoadingFeature,
  isLoadingSpendingSummary,
  isError,
  merchantOrder,
  onRefreshInvoice,
  status,
  refund
}) {
  const { t } = useTranslation();
  const { data: user } = useGetUser();
  const [totalEstimatedSpendModal, setTotalEstimatedSpendModal] = useState(false);
  const [spentToDateModal, setSpentToDateModal] = useState(false);

  const isManagedAccount =
    user?.organization?.configs?.accountType === ORGANIZATION_TYPES.managedAccount;

  const minEstimatedCharge = estimatedCharges?.serviceCharges?.min;
  const maxEstimatedCharge = estimatedCharges?.serviceCharges?.max;
  const hasMinMaxEstimatedCharge =
    typeof minEstimatedCharge === 'number' && typeof maxEstimatedCharge === 'number';
  const shouldShowEstimatedChargeRange = minEstimatedCharge === 0 && maxEstimatedCharge !== 0;

  const getValue = value => {
    const hasEstimatedCharge = !!adjustedEstimatedCharge || !!hasMinMaxEstimatedCharge;

    if (isError) {
      return 'N/A';
    } else if (isLoadingSpendingSummary || (!hasEstimatedCharge && !refund)) {
      return <SkeletonLoaders.CostSummaryLoader />;
    }
    return formatCurrency(value);
  };

  const hideCostAccordion = [
    RECOGNITION_STATUSES.expired,
    AUTOMATION_STATUSES.deactivated
  ].includes(status);

  return (
    <S.CostSummaryCard>
      <S.CostSummaryTitle>
        {isManagedAccount && !isCCPayment ? (
          <>
            <S.CostSummaryTitleLeft>
              <Typography variant="body1Bold">{t('costSummary')}</Typography>
              <VerticalDivider $height="16px" />
              <Button onClick={onRefreshInvoice} variant="link">
                {t('refresh')}
              </Button>
            </S.CostSummaryTitleLeft>
            <OrderStatusBadge
              status={
                merchantOrder?.invoice?.created
                  ? ORDER_STATUSES.invoiceCreated
                  : ORDER_STATUSES.noInvoiceYet
              }
            />
          </>
        ) : (
          <Typography variant="body1Bold">{t('costSummary')}</Typography>
        )}
      </S.CostSummaryTitle>
      <S.Content>
        <S.SpentToDateRow>
          <S.CostAccordion
            title={`${t('spentToDate')}:`}
            description={
              <Button
                onClick={e => {
                  e.stopPropagation();
                  setSpentToDateModal(true);
                }}
                variant="link"
                style={{
                  color: 'var(--color-neutral-400)',
                  fontSize: '12px'
                }}
                color="neutral"
              >
                {t('howIsThisCalculated')}
              </Button>
            }
            endContent={getValue(spendingSummary?.total)}
            variant="contained"
          >
            <S.Row>
              <Typography variant="body2SemiBold">{t('items') + ':'}</Typography>
              <S.Value>{getValue(spendingSummary?.merchandise)}</S.Value>
            </S.Row>

            <S.Row>
              <Typography variant="body2SemiBold">{t('shippingAndServiceFee') + ':'}</Typography>
              <S.Value>{getValue(spendingSummary?.serviceCharge)}</S.Value>
            </S.Row>

            <S.Row>
              <Typography variant="body2SemiBold">{t('tax') + ':'}</Typography>
              <S.Value>{getValue(spendingSummary?.estimatedTax)}</S.Value>
            </S.Row>
          </S.CostAccordion>
        </S.SpentToDateRow>
        {!hideCostAccordion && (
          <S.CostAccordion
            title={
              <Typography as="div" variant="body2Bold" style={{ display: 'flex', gap: 4 }}>
                {t('totalEstimatedSpend')}:
                {featureType === 'automation' && (
                  <Typography variant="body2" color="fg" emphasis="weaker">
                    ({t('perRecipient').toLocaleLowerCase()})
                  </Typography>
                )}
              </Typography>
            }
            description={
              <Button
                onClick={e => {
                  e.stopPropagation();
                  setTotalEstimatedSpendModal(true);
                }}
                variant="link"
                style={{
                  color: 'var(--color-neutral-400)',
                  fontSize: '12px'
                }}
                color="neutral"
              >
                {t('howOurPriceWork')}
              </Button>
            }
            endContent={getValue(adjustedEstimatedCharge?.total || estimatedCharges?.total)}
            endContentProps={{ color: 'success' }}
            variant="outlined"
          >
            <S.Row>
              <Typography variant="body2SemiBold">{t('itemsEstimate') + ':'}</Typography>
              <S.Value>
                {getValue(adjustedEstimatedCharge?.merchandise || estimatedCharges?.merchandise)}
              </S.Value>
            </S.Row>
            <S.Row>
              <Typography variant="body2SemiBold">
                {t('estimatedServiceAndShippingFees') + ':'}
                {shouldShowEstimatedChargeRange && (
                  <Typography variant="caption1" color="fg" emphasis="weak">
                    {t('serviceChargeMessageForFreeShip')}
                  </Typography>
                )}
              </Typography>
              {shouldShowEstimatedChargeRange ? (
                <S.Value>
                  {getValue(minEstimatedCharge)} - {getValue(maxEstimatedCharge)}
                </S.Value>
              ) : (
                <S.Value>
                  {getValue(
                    adjustedEstimatedCharge?.serviceCharge || estimatedCharges?.serviceCharge
                  )}
                </S.Value>
              )}
            </S.Row>
            <S.Row>
              <Typography variant="body2SemiBold">{t('estimatedTax') + ':'}</Typography>
              <S.Value>
                {getValue(adjustedEstimatedCharge?.estimatedTax || estimatedCharges?.estimatedTax)}
              </S.Value>
            </S.Row>
          </S.CostAccordion>
        )}
        {isCCPayment && (
          <CcPaymentNotifications
            isFetching={isLoadingSpendingSummary || isLoadingFeature}
            refund={{
              total: refund?.total,
              estimatedAt: refund?.estimatedAt
            }}
            status={status}
            authorization={authorization}
          />
        )}
      </S.Content>

      {featureType === 'automation' ? (
        <TotalEstimatedSpendAutomationModal
          isModalOpen={totalEstimatedSpendModal}
          onModalOpen={() => setTotalEstimatedSpendModal(false)}
        />
      ) : (
        <TotalEstimatedSpendModal
          isCCPayment={isCCPayment}
          isModalOpen={totalEstimatedSpendModal}
          onModalOpen={() => setTotalEstimatedSpendModal(false)}
        />
      )}
      <SpentToDateModal
        isCCPayment={isCCPayment}
        isModalOpen={spentToDateModal}
        onModalOpen={() => setSpentToDateModal(false)}
      />
    </S.CostSummaryCard>
  );
}

export { LiveCostSummary };
